import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BsCheckCircle, BsArrowLeft, BsXCircle, BsClock } from 'react-icons/bs';
import { firebase } from '../firebaseConfig';

const VERIFY_URL = 'https://veth-admin.vercel.app/api/verify-payment';

// Get the current Firebase user, waiting up to 4s for auth state to restore
const getAuthUser = () =>
  new Promise((resolve) => {
    const user = firebase.auth().currentUser;
    if (user) return resolve(user);
    const timeout = setTimeout(() => resolve(null), 4000);
    const unsub = firebase.auth().onAuthStateChanged((u) => {
      clearTimeout(timeout);
      unsub();
      resolve(u || null);
    });
  });

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [state, setState] = useState('loading');
  const [orderDetails, setOrderDetails] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const transactionId = params.get('transaction_id');
      const txRef = params.get('tx_ref');
      const status = params.get('status');

      console.log('[PaymentSuccess] URL params:', { transactionId, txRef, status });

      // Flutterwave sends status='completed' for successful payments in redirect URL
      // Only block if explicitly failed or cancelled
      if (status === 'failed' || status === 'cancelled') {
        setState('failed');
        setErrorMsg('Payment was cancelled or failed. No money was taken.');
        return;
      }

      if (!transactionId && !txRef) {
        setState('no_params');
        return;
      }

      // Get Firebase auth user (for linking order to account)
      const authUser = await getAuthUser();
      console.log('[PaymentSuccess] Auth user:', authUser?.uid || 'guest');

      // Try to get extra order data from localStorage (customer info, products)
      // If not available (cleared by browser redirect), the API still saves with basic info
      let orderData = null;
      try {
        const raw = localStorage.getItem('currentOrder') || localStorage.getItem('pendingPayment');
        if (raw) orderData = JSON.parse(raw);
      } catch (_) {}

      // Build payload for the verify API — include userId and userEmail for account linking
      const payload = {
        transaction_id: transactionId,
        tx_ref: txRef,
        amount: orderData?.amount || params.get('amount') || 0,
        currency: 'NGN',
        userId: authUser?.uid || null,
        userEmail: authUser?.email || orderData?.customerData?.email || null,
        customer: orderData?.customerData
          ? {
              name: orderData.customerData.name,
              email: orderData.customerData.email,
              phone_number: orderData.customerData.phone_number,
            }
          : authUser
          ? { name: authUser.displayName || '', email: authUser.email || '' }
          : null,
        items: orderData?.items || null,
        productData: orderData?.productData || null,
        orderType: orderData?.items?.length > 0 ? 'cart' : orderData?.productData ? 'single' : 'unknown',
        quantity: orderData?.quantity || 1,
      };

      console.log('[PaymentSuccess] Calling verify-payment API...');

      try {
        const res = await fetch(VERIFY_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        const data = await res.json();
        console.log('[PaymentSuccess] API response:', data);

        if (data.success) {
          setOrderDetails(data);
          setState('success');
          localStorage.removeItem('currentOrder');
          localStorage.removeItem('pendingPayment');
        } else {
          // API failed but payment was confirmed by Flutterwave redirect
          // Show success anyway — webhook will handle the backend save
          console.warn('[PaymentSuccess] API failed but payment confirmed:', data.error);
          setOrderDetails({ transactionId: transactionId || txRef, amount: payload.amount });
          setState('success');
        }
      } catch (err) {
        // Network error — still show success, webhook handles the backend
        console.error('[PaymentSuccess] Network error:', err);
        setOrderDetails({ transactionId: transactionId || txRef, amount: payload.amount });
        setState('success');
      }
    };

    run();
  }, []);

  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-white dark:bg-gradient-to-r from-black to-amber-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-amber-900/20 rounded-2xl shadow-lg p-8 text-center">
          <div className="flex justify-center mb-6">
            <BsClock className="text-6xl text-amber-500 animate-pulse" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Confirming your payment...
          </h1>
          <p className="text-gray-600 dark:text-amber-200">Please wait a moment.</p>
        </div>
      </div>
    );
  }

  if (state === 'failed' || state === 'no_params') {
    return (
      <div className="min-h-screen bg-white dark:bg-gradient-to-r from-black to-amber-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-amber-900/20 rounded-2xl shadow-lg p-8 text-center border border-red-200 dark:border-red-700">
          <div className="flex justify-center mb-6">
            <BsXCircle className="text-6xl text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            {state === 'no_params' ? 'No Payment Found' : 'Payment Not Completed'}
          </h1>
          <p className="text-gray-600 dark:text-amber-200 mb-6 text-sm">
            {errorMsg || 'Please return to the shop to try again.'}
          </p>
          <button
            onClick={() => navigate('/shop')}
            className="w-full bg-amber-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-amber-700 transition-colors"
          >
            Go to Shop
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gradient-to-r from-black to-amber-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white dark:bg-amber-900/20 rounded-2xl shadow-lg dark:shadow-amber-700/30 p-8 border border-amber-200 dark:border-amber-700 text-center">
        <div className="flex justify-center mb-6">
          <BsCheckCircle className="text-6xl text-green-500" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Payment Successful!
        </h1>
        <p className="text-gray-600 dark:text-amber-200 mb-6 text-sm">
          Your order has been placed. You will receive a confirmation shortly.
        </p>

        {orderDetails && (
          <div className="bg-gray-50 dark:bg-amber-900/20 rounded-lg p-4 mb-6 text-left space-y-2">
            {orderDetails.productPaidFor && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-amber-200">Product:</span>
                <span className="text-gray-900 dark:text-white font-medium text-right max-w-[60%]">
                  {orderDetails.productPaidFor}
                </span>
              </div>
            )}
            {orderDetails.amount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-amber-200">Amount paid:</span>
                <span className="text-gray-900 dark:text-white font-semibold">
                  ₦{Number(orderDetails.amount).toLocaleString()}
                </span>
              </div>
            )}
            {orderDetails.customer?.name && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-amber-200">Name:</span>
                <span className="text-gray-900 dark:text-white">{orderDetails.customer.name}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-amber-200">Status:</span>
              <span className="text-green-600 dark:text-green-400 font-semibold">Confirmed ✓</span>
            </div>
            {orderDetails.transactionId && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-amber-200">Ref:</span>
                <span className="text-gray-500 dark:text-amber-300 font-mono text-xs truncate max-w-[60%]">
                  {orderDetails.transactionId}
                </span>
              </div>
            )}
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={() => navigate('/profile')}
            className="w-full bg-amber-600 dark:bg-amber-700 text-white py-3 px-6 rounded-lg font-semibold hover:bg-amber-700 transition-colors"
          >
            View My Orders
          </button>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-gray-100 dark:bg-amber-800/50 text-gray-800 dark:text-amber-200 py-3 px-6 rounded-lg font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
          >
            <BsArrowLeft />
            Continue Shopping
          </button>
        </div>
        <p className="mt-4 text-xs text-gray-400 dark:text-amber-400">
          Track your order under Profile → Orders
        </p>
      </div>
    </div>
  );
};

export default PaymentSuccess;
